/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import { ConnectionConfig, WebSocketConnection } from './websocket-registry';
import { DiConfigService } from '@app/di/di-config.service';
import { appConstants } from '@app/common/app-constants';

const log = new Logger('WebsocketHandlerService');

@Injectable({
  providedIn: 'root'
})
export class WebsocketHandlerService {
  private connections: Map<string, WebSocketConnection> = new Map();

  constructor(private readonly diConfigService: DiConfigService) {}

  /**
   * Registers a new WebSocket connection with the specified configuration.
   * Creates and manages a WebSocket connection with retry logic and event handlers.
   *
   * @param application - The `ConnectionConfig` object containing ID, URL, handlers, and optional retry settings
   */
  async registerConnection(application: ConnectionConfig) {
    if (!(application.id && application.handlers)) {
      log.debug('Invalid arguments for register connection', application);
      return;
    }
    if (this.connections.has(application.id)) {
      log.error('Connection with ID: ', application.id, ' already exists');
      return;
    }
    try {
      // Initialize retry counter for this connection
      application.retryCount = 0;
      await this.createWebSocketConnection(application);
    } catch (error) {
      throw error;
    }
  }

  private async createWebSocketConnection(application: ConnectionConfig, isRetry = false) {
    const protocol = [];
    try {
      // Set/Reset retry count based on isRetry being called with;
      application.retryCount = isRetry ? application.retryCount + 1 : 0;
      // Check for application to use websocket secure protocol header
      if (application.useSecureProtocol) {
        const token = await this.diConfigService.getJwtToken();
        // Check for application having prefix for secure websocket protocol
        if (application.secureProtocolPrefix) {
          protocol.push(application.secureProtocolPrefix);
        }
        // Check for application having prefix for secure websocket token protocol
        if (application.secureProtocolTokenPrefix) {
          protocol.push(application.secureProtocolTokenPrefix + token);
        } else {
          protocol.push(token);
        }
      }

      const socket = new WebSocket(application.url, protocol);

      const connection: WebSocketConnection = {
        ...application,
        socket,
        sendMessage: (message: any) => this.sendMessage(socket, application.id, message),
        close: () => this.unregister(application.id)
      };

      this.setupHandlers(connection);
      // Store in registry
      this.connections.set(application.id, connection);
    } catch (error) {
      log.error(`Failed to Create websocket connection: Application: ${application.id}`, error);
      throw error;
    }
  }

  private sendMessage(socket: WebSocket, id: string, message: any): boolean {
    let result = true;
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(typeof message === 'string' ? message : JSON.stringify(message));
    } else {
      result = false;
      log.debug(`Cannot send message - connection ${id} is not open`);
    }
    return result;
  }

  // Setup handler callbacks for Socket Update
  private setupHandlers(connection: WebSocketConnection) {
    connection.socket.onmessage = event => {
      connection.handlers.onMessage(event);
      if (connection.id === appConstants.APP_ID.GENESIS) {
        this.handleConnectionOpen(connection, event);
      }
    };

    connection.socket.onerror = event => {
      log.error(`${connection.id}: Websocket Error`, event);
      console.error(`[WFM][FHIRcast][${connection.id}] WebSocket error.`, event);
      // Skipped retry for error, as WebSocket closes after an error
      // retry mechanism is handled in onclose
      if (!connection?.maxRetry) {
        connection.handlers.onError(event);
      }
    };

    connection.socket.onclose = event => {
      log.debug(`${connection.id}: Websocket Close`, event);
      if (connection?.maxRetry) {
        this.handleConnectionRetry(connection, event);
      } else {
        connection.handlers?.onClose(event);
      }
    };

    connection.socket.onopen = event => {
      log.debug(`${connection.id}: Websocket Opened`, event);
      console.log(`[WFM][FHIRcast][${connection.id}] WebSocket opened. url=${connection.url}`);
      if (connection.handlers.onOpen && connection.id !== appConstants.APP_ID.GENESIS) {
        connection.handlers.onOpen(event);
      }
    };
  }

  private handleConnectionOpen(connection: WebSocketConnection, event: MessageEvent): void {
    const message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    if (Array.isArray(message) && message?.[0]?.['hub.mode'] === 'subscribe') {
      if (connection.id === appConstants.APP_ID.GENESIS) {
        connection.handlers.onOpen({ ...event, type: 'open' });
      }
      connection.retryCount = 0;
    }
  }

  private handleConnectionRetry(connection: WebSocketConnection, event: CloseEvent) {
    if (connection.socket.readyState !== WebSocket.CLOSED) {
      connection.socket.onclose = null;
      connection.socket.close();
    }

    if (connection.retryCount === connection.maxRetry) {
      console.error(`[WFM][FHIRcast][${connection.id}] WebSocket max retries (${connection.maxRetry}) reached. Giving up.`);
      connection.handlers?.onClose(event);
      return;
    }
    connection.handlers?.onClose(event, true);

    log.debug(
      `WebSocket connection ${connection.id} failed. Retry attempt ${connection.retryCount + 1} of ${
        connection.maxRetry
      }`
    );
    console.warn(`[WFM][FHIRcast][${connection.id}] WebSocket closed. Retry attempt ${connection.retryCount + 1} of ${connection.maxRetry} in ${connection.retryPeriod * (connection.retryCount || 1)}ms.`);

    const timer = setTimeout(() => {
      log.debug(`Retrying WebSocket connection for ${connection.id}`);
      console.log(`[WFM][FHIRcast][${connection.id}] Retrying WebSocket connection now (attempt ${connection.retryCount + 1}).`);
      this.createWebSocketConnection(connection, true);
      clearTimeout(timer);
    }, connection.retryPeriod * (connection.retryCount || 1));
  }

  /**
   * Retrieves an existing WebSocket connection by its unique identifier.
   *
   * @param id - The unique identifier of the connection to retrieve
   * @returns The WebSocketConnection object if found, undefined otherwise
   */
  getConnection(id: string): WebSocketConnection | undefined {
    return this.connections.get(id);
  }

  /**
   * Checks if a WebSocket connection exists for the given identifier.
   *
   * @param id - The unique identifier of the connection to check
   * @returns true if a connection with the specified ID exists, false otherwise
   */
  hasConnection(id: string): boolean {
    return this.connections.has(id);
  }

  /**
   * Unregisters and closes a WebSocket connection by its unique identifier.
   * Properly cleans up the connection by removing event handlers, closing the socket,
   * and removing the connection from internal registries.
   *
   * @param id - The unique identifier of the connection to unregister
   * @returns true if the connection was successfully unregistered, false if connection was not found
   */
  unregister(id: string): boolean {
    log.info('Initiating Close for:', id);
    const connection = this.connections.get(id);
    if (connection) {
      if (connection.socket.readyState !== WebSocket.CLOSED) {
        connection.socket.onmessage = null;
        connection.socket.onerror = null;
        connection.socket.onclose = null;
        connection.socket.onopen = null;
        connection.socket.close();
        log.info('Closed connection for:', id);
      }
      this.connections.delete(id);
      // Remove from retry map as well
      log.info(`App : ${id}: Unregistered`);
      return true;
    }
    log.warn(`App : ${id}: Already unregistered`);
    return false;
  }
}
